
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { profileFormSchema, ProfileFormValues } from '@/schemas/profileSchema';
import { useToast } from '@/hooks/use-toast';

export const useProfileData = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userInitials, setUserInitials] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const { toast } = useToast();
  const supabase = useSupabaseClient();

  // Create form
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      title: '',
      phone: '',
      address: '',
      avatar_url: '',
    },
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          setUserId(user.id);
          // First set form values based on auth user metadata
          form.setValue('name', user.user_metadata?.name || '');
          form.setValue('email', user.email || '');
          form.setValue('company', user.user_metadata?.company || '');
          form.setValue('title', user.user_metadata?.title || '');
          form.setValue('phone', user.user_metadata?.phone || '');
          form.setValue('address', user.user_metadata?.address || '');
          form.setValue('avatar_url', user.user_metadata?.avatar_url || '');
          setAvatarUrl(user.user_metadata?.avatar_url || null);
          
          // Then try to get profile data from profiles table
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .maybeSingle();
            
          if (profileData) {
            // Override form values with profile data if it exists
            if (profileData.name) form.setValue('name', profileData.name);
            if (profileData.company) form.setValue('company', profileData.company);
            if (profileData.title) form.setValue('title', profileData.title);
            if (profileData.phone) form.setValue('phone', profileData.phone);
            if (profileData.avatar_url) {
              form.setValue('avatar_url', profileData.avatar_url);
              setAvatarUrl(profileData.avatar_url);
            }
          } else if (profileError) {
            console.error('Error fetching profile:', profileError);
          }
          
          // Generate initials from name
          if (user.user_metadata?.name) {
            const nameParts = user.user_metadata.name.split(' ');
            if (nameParts.length > 1) {
              setUserInitials(`${nameParts[0][0]}${nameParts[1][0]}`);
            } else if (nameParts.length === 1) {
              setUserInitials(nameParts[0].substring(0, 2));
            }
          } else {
            // Fallback to email
            setUserInitials(user.email?.substring(0, 2).toUpperCase() || 'SU');
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setError('Det gick inte att hämta profilinformation. Försök igen senare.');
      }
    };

    fetchUserProfile();
  }, [supabase, form]);

  const onSubmit = async (values: ProfileFormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      if (!userId) {
        throw new Error('Användare saknas. Logga in igen och försök på nytt.');
      }

      // Update user metadata in auth.users
      const { error: userUpdateError } = await supabase.auth.updateUser({
        data: {
          name: values.name,
          company: values.company,
          title: values.title,
          phone: values.phone,
          address: values.address,
          avatar_url: values.avatar_url || avatarUrl,
        }
      });

      if (userUpdateError) throw userUpdateError;

      // Update or insert profile data in profiles table
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .upsert({
          id: userId,
          name: values.name,
          company: values.company,
          title: values.title,
          phone: values.phone,
          avatar_url: values.avatar_url || avatarUrl,
        });

      if (profileUpdateError) throw profileUpdateError;

      toast({
        title: "Profil uppdaterad",
        description: "Dina profiluppgifter har sparats.",
      });
    } catch (error: any) {
      console.error('Profile update error:', error);
      setError(error.message || "Kunde inte uppdatera profilen. Försök igen.");
      toast({
        title: "Ett fel uppstod",
        description: error.message || "Kunde inte uppdatera profilen. Försök igen.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle avatar updates
  const onAvatarUpdate = async (url: string) => {
    setAvatarUrl(url);
    form.setValue('avatar_url', url);
    
    // Update user metadata and profile table with new avatar URL
    try {
      if (!userId) {
        throw new Error('Användare saknas. Logga in igen och försök på nytt.');
      }

      // Update user metadata in auth.users
      const { error: userUpdateError } = await supabase.auth.updateUser({
        data: {
          avatar_url: url,
        }
      });

      if (userUpdateError) throw userUpdateError;

      // Update profile data in profiles table
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .upsert({
          id: userId,
          avatar_url: url,
        });

      if (profileUpdateError) throw profileUpdateError;
      
      toast({
        title: "Profilbild uppdaterad",
        description: "Din profilbild har sparats.",
      });
    } catch (error: any) {
      console.error('Avatar update error:', error);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte uppdatera profilbilden. Försök igen.",
        variant: "destructive",
      });
    }
  };

  return {
    form,
    onSubmit,
    isLoading,
    error,
    userInitials,
    userId,
    avatarUrl,
    onAvatarUpdate,
  };
};
